import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '../lib/api';
import toast from 'react-hot-toast';

// -------------------------------------------------------------
// 1. Types & Interfaces
// -------------------------------------------------------------

export interface SSOProvider {
  id: string;
  name: string;
  type: 'saml' | 'oidc';
  entityId: string; // or Client ID
  clientSecret?: string; // masked
  metadataUrl: string;
  acsUrl: string;
  attributeMapping: {
    email: string;
    firstName: string;
    lastName: string;
    roles?: string;
  };
  defaultRole: string;
  enforceDomain: string; // e.g. "company.com"
  enabled: boolean;
  status: 'active' | 'inactive';
  lastTestedAt?: string;
  testLatencyMs?: number;
  createdAt: string;
}

export interface MFASettings {
  enabled: boolean;
  requiredRoles: string[]; // ['super_admin', 'hr_admin', etc.]
  methods: {
    totp: boolean;
    sms: boolean;
    emailOtp: boolean;
    webAuthn: boolean; // Hardware key
  };
  gracePeriodDays: number; // 0, 7, 14, 30
  rememberDeviceDays: number; // 30
  backupCodesCount: number; // 10
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSymbols: boolean;
  expiryDays: number; // 90
  preventReuseCount: number; // 5
  maxFailedAttempts: number; // 5
  lockoutDurationMinutes: number; // 30
  sessionTimeoutMinutes: number; // 30
}

export interface ABACCondition {
  field: string; // e.g. "user.department", "resource.sensitivity", "env.network"
  operator: 'equals' | 'not_equals' | 'contains' | 'in' | 'not_in';
  value: string;
}

export interface ABACPolicy {
  id: string;
  name: string;
  description: string;
  effect: 'ALLOW' | 'DENY';
  priority: number; // 1 = highest
  status: 'active' | 'inactive';
  logic: 'AND' | 'OR';
  conditions: ABACCondition[];
  affectedUsersCount: number;
  updatedAt: string;
  updatedBy: string;
}

export interface EncryptionKey {
  id: string;
  keyId: string;
  algorithm: 'AES-256-GCM' | 'RSA-4096' | 'ChaCha20-Poly1305';
  scope: 'database' | 'storage' | 'tokens' | 'backups';
  status: 'active' | 'retired' | 'revoked';
  rotationScheduleDays: number; // 90
  createdAt: string;
  lastRotatedAt: string;
}

export interface EncryptionSettings {
  atRestEnabled: boolean;
  hsmActive: boolean;
  tlsMinVersion: 'TLS 1.2' | 'TLS 1.3';
  fieldLevelEncryptedColumns: string[];
}

export interface MaskingRule {
  id: string;
  columnName: string;
  tableName: string;
  maskType: 'full' | 'partial' | 'hash' | 'tokenize';
  allowedRoles: string[]; // e.g. ['super_admin', 'hr_admin']
  sampleOriginal: string;
  sampleMasked: string;
  status: 'active' | 'inactive';
  updatedAt: string;
}

export interface DLPRule {
  id: string;
  name: string;
  patternType: 'credit_card' | 'ssn_pan' | 'email' | 'phone' | 'custom_regex';
  customRegex?: string;
  scope: ('uploads' | 'exports' | 'emails' | 'api_responses')[];
  action: 'log' | 'warn' | 'mask' | 'block';
  severity: 'low' | 'medium' | 'high' | 'critical';
  enabled: boolean;
  matchesCount: number;
  updatedAt: string;
}

export interface DLPIncident {
  id: string;
  ruleName: string;
  patternType: string;
  userEmail: string;
  scope: string;
  actionTaken: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'investigating' | 'resolved';
  timestamp: string;
  details: string;
}

export interface IPRule {
  id: string;
  type: 'allow' | 'block';
  ipOrCidr: string;
  note: string;
  expiresAt: string; // ISO date or "Never"
  addedBy: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

export interface GeoFenceRule {
  id: string;
  type: 'allow' | 'block';
  countries: string[]; // ISO country codes: ['US', 'GB', 'IN', 'CA']
  action: 'block' | 'require_mfa' | 'alert';
  exceptions: {
    roles: string[];
    users: string[];
  };
  status: 'active' | 'inactive';
  updatedAt: string;
}

export interface AnomalySettings {
  impossibleTravel: { enabled: boolean; sensitivity: 'low' | 'medium' | 'high'; action: 'alert' | 'force_mfa' | 'lock_account' };
  unusualHours: { enabled: boolean; sensitivity: 'low' | 'medium' | 'high'; action: 'alert' | 'force_mfa' | 'terminate_session' };
  newDevice: { enabled: boolean; sensitivity: 'low' | 'medium' | 'high'; action: 'alert' | 'force_mfa' };
  bruteForce: { enabled: boolean; sensitivity: 'low' | 'medium' | 'high'; action: 'lock_account' | 'alert' };
  massDataExport: { enabled: boolean; sensitivity: 'low' | 'medium' | 'high'; action: 'block' | 'alert' };
}

export interface AnomalyAlert {
  id: string;
  type: string;
  riskScore: number; // 0-100
  userEmail: string;
  location: string;
  ipAddress: string;
  status: 'open' | 'investigating' | 'resolved';
  detectedAt: string;
  actionTaken: string;
}

export interface SecurityAuditItem {
  id: string;
  action: string;
  section: 'auth' | 'abac' | 'data_protection' | 'threat_protection' | 'roles' | 'users';
  performedBy: string;
  target: string;
  oldValue?: string;
  newValue?: string;
  timestamp: string;
}

// -------------------------------------------------------------
// 2. Initial Seed Data
// -------------------------------------------------------------

const INITIAL_SSO_PROVIDERS: SSOProvider[] = [
  {
    id: 'sso-1',
    name: 'Okta Enterprise Workforce',
    type: 'saml',
    entityId: 'https://okta.company.com/app/teamone/sso',
    clientSecret: '••••••••••••••••••••',
    metadataUrl: 'https://okta.company.com/app/teamone/sso/metadata',
    acsUrl: 'https://teamone.app/api/auth/callback/saml',
    attributeMapping: {
      email: 'user.email',
      firstName: 'user.firstName',
      lastName: 'user.lastName',
      roles: 'user.groups',
    },
    defaultRole: 'Employee',
    enforceDomain: 'teamone.com',
    enabled: true,
    status: 'active',
    lastTestedAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    testLatencyMs: 42,
    createdAt: '2026-01-15T09:00:00Z',
  },
  {
    id: 'sso-2',
    name: 'Azure Active Directory OIDC',
    type: 'oidc',
    entityId: '04a39f82-55bb-4791-bf96-9f8e434f3c09',
    clientSecret: '••••••••••••••••••••',
    metadataUrl: 'https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration',
    acsUrl: 'https://teamone.app/api/auth/callback/oidc',
    attributeMapping: {
      email: 'preferred_username',
      firstName: 'given_name',
      lastName: 'family_name',
      roles: 'roles',
    },
    defaultRole: 'Employee',
    enforceDomain: 'microsoft-partner.org',
    enabled: false,
    status: 'inactive',
    lastTestedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    testLatencyMs: 88,
    createdAt: '2026-03-10T14:30:00Z',
  },
];

const INITIAL_MFA: MFASettings = {
  enabled: true,
  requiredRoles: ['super_admin', 'hr_admin', 'reporting_manager'],
  methods: {
    totp: true,
    sms: false,
    emailOtp: true,
    webAuthn: true,
  },
  gracePeriodDays: 7,
  rememberDeviceDays: 30,
  backupCodesCount: 10,
};

const INITIAL_PW_POLICY: PasswordPolicy = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
  expiryDays: 90,
  preventReuseCount: 5,
  maxFailedAttempts: 5,
  lockoutDurationMinutes: 30,
  sessionTimeoutMinutes: 30,
};

const INITIAL_ABAC_POLICIES: ABACPolicy[] = [
  {
    id: 'abac-1',
    name: 'Deny Non-VPN Access to Production Financials',
    description: 'Enforces strict corporate VPN or office network requirement for high sensitivity Money/Finance records',
    effect: 'DENY',
    priority: 1,
    status: 'active',
    logic: 'AND',
    conditions: [
      { field: 'resource.sensitivity', operator: 'equals', value: 'High' },
      { field: 'env.network', operator: 'not_equals', value: 'Corporate VPN' },
    ],
    affectedUsersCount: 14,
    updatedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    updatedBy: 'Admin (System)',
  },
  {
    id: 'abac-2',
    name: 'Confidential HR Records - Regional Geo-Fence',
    description: 'Allows HR managers to view sensitive employee files only if located within their operational region',
    effect: 'ALLOW',
    priority: 2,
    status: 'active',
    logic: 'AND',
    conditions: [
      { field: 'user.department', operator: 'equals', value: 'Human Resources' },
      { field: 'user.location', operator: 'in', value: 'US, UK, IN' },
      { field: 'env.time', operator: 'equals', value: 'Business Hours' },
    ],
    affectedUsersCount: 8,
    updatedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    updatedBy: 'Admin (System)',
  },
  {
    id: 'abac-3',
    name: 'Deny Mobile/BYOD Export for Customer Data',
    description: 'Prevents mass export of customer lists from unmanaged or BYOD devices',
    effect: 'DENY',
    priority: 3,
    status: 'active',
    logic: 'AND',
    conditions: [
      { field: 'resource.type', operator: 'equals', value: 'Customer PII' },
      { field: 'env.device', operator: 'equals', value: 'BYOD' },
    ],
    affectedUsersCount: 32,
    updatedAt: new Date(Date.now() - 86400000 * 7).toISOString(),
    updatedBy: 'Security Officer',
  },
];

const INITIAL_ENCRYPTION_SETTINGS: EncryptionSettings = {
  atRestEnabled: true,
  hsmActive: true,
  tlsMinVersion: 'TLS 1.3',
  fieldLevelEncryptedColumns: ['ssn_pan', 'bank_account_number', 'salary_amount', 'medical_notes'],
};

const INITIAL_KEYS: EncryptionKey[] = [
  {
    id: 'key-1',
    keyId: 'KMS-MASTER-DATABASE-2026',
    algorithm: 'AES-256-GCM',
    scope: 'database',
    status: 'active',
    rotationScheduleDays: 90,
    createdAt: '2026-01-01T00:00:00Z',
    lastRotatedAt: '2026-07-01T00:00:00Z',
  },
  {
    id: 'key-2',
    keyId: 'KMS-VAULT-DOCUMENTS-2026',
    algorithm: 'AES-256-GCM',
    scope: 'storage',
    status: 'active',
    rotationScheduleDays: 180,
    createdAt: '2026-02-15T00:00:00Z',
    lastRotatedAt: '2026-08-15T00:00:00Z',
  },
  {
    id: 'key-3',
    keyId: 'KMS-JWT-SIGNER-ROTATED-V1',
    algorithm: 'RSA-4096',
    scope: 'tokens',
    status: 'retired',
    rotationScheduleDays: 90,
    createdAt: '2025-10-01T00:00:00Z',
    lastRotatedAt: '2026-01-01T00:00:00Z',
  },
];

const INITIAL_MASKING_RULES: MaskingRule[] = [
  {
    id: 'mask-1',
    columnName: 'credit_card_number',
    tableName: 'billing_payment_methods',
    maskType: 'partial',
    allowedRoles: ['super_admin'],
    sampleOriginal: '4532-8921-9923-1124',
    sampleMasked: '•••• •••• •••• 1124',
    status: 'active',
    updatedAt: '2026-08-20T11:00:00Z',
  },
  {
    id: 'mask-2',
    columnName: 'national_id_ssn',
    tableName: 'people_employees',
    maskType: 'partial',
    allowedRoles: ['super_admin', 'hr_admin'],
    sampleOriginal: '982-12-8849',
    sampleMasked: '•••-••-8849',
    status: 'active',
    updatedAt: '2026-08-22T09:30:00Z',
  },
  {
    id: 'mask-3',
    columnName: 'salary_base_amount',
    tableName: 'payroll_records',
    maskType: 'full',
    allowedRoles: ['super_admin', 'hr_admin'],
    sampleOriginal: '$124,500.00',
    sampleMasked: '••••••••',
    status: 'active',
    updatedAt: '2026-08-25T14:15:00Z',
  },
  {
    id: 'mask-4',
    columnName: 'personal_email',
    tableName: 'candidates',
    maskType: 'tokenize',
    allowedRoles: ['super_admin', 'hr_admin', 'recruiter'],
    sampleOriginal: 'sarah.connor@skyline.org',
    sampleMasked: 's•••••r@skyline.org',
    status: 'active',
    updatedAt: '2026-09-01T10:00:00Z',
  },
];

const INITIAL_DLP_RULES: DLPRule[] = [
  {
    id: 'dlp-1',
    name: 'Credit Card / PCI Data Protection',
    patternType: 'credit_card',
    scope: ['uploads', 'exports', 'emails', 'api_responses'],
    action: 'block',
    severity: 'critical',
    enabled: true,
    matchesCount: 19,
    updatedAt: '2026-09-10T12:00:00Z',
  },
  {
    id: 'dlp-2',
    name: 'National Identity Number (SSN, Aadhaar, PAN)',
    patternType: 'ssn_pan',
    scope: ['exports', 'emails', 'api_responses'],
    action: 'mask',
    severity: 'high',
    enabled: true,
    matchesCount: 42,
    updatedAt: '2026-09-12T15:20:00Z',
  },
  {
    id: 'dlp-3',
    name: 'Internal Project Secrets & API Keys',
    patternType: 'custom_regex',
    customRegex: '(sk_live_[0-9a-zA-Z]{24}|ghp_[0-9a-zA-Z]{36}|AIza[0-9A-Za-z-_]{35})',
    scope: ['uploads', 'emails'],
    action: 'block',
    severity: 'critical',
    enabled: true,
    matchesCount: 3,
    updatedAt: '2026-09-15T08:45:00Z',
  },
];

const INITIAL_DLP_INCIDENTS: DLPIncident[] = [
  {
    id: 'inc-101',
    ruleName: 'Credit Card / PCI Data Protection',
    patternType: 'credit_card',
    userEmail: 'dev.contractor@partner.net',
    scope: 'exports',
    actionTaken: 'Blocked download transaction',
    severity: 'critical',
    status: 'open',
    timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
    details: 'User attempted to export raw invoice dump containing 16 unmasked Visa card numbers.',
  },
  {
    id: 'inc-102',
    ruleName: 'National Identity Number (SSN, Aadhaar, PAN)',
    patternType: 'ssn_pan',
    userEmail: 'recruiter.intern@teamone.com',
    scope: 'emails',
    actionTaken: 'Automated field masking applied',
    severity: 'high',
    status: 'resolved',
    timestamp: new Date(Date.now() - 3600000 * 22).toISOString(),
    details: 'Outgoing candidate summary email contained candidate SSN in body text.',
  },
  {
    id: 'inc-103',
    ruleName: 'Internal Project Secrets & API Keys',
    patternType: 'custom_regex',
    userEmail: 'qa.engineer@teamone.com',
    scope: 'uploads',
    actionTaken: 'Blocked file upload & logged alert',
    severity: 'critical',
    status: 'investigating',
    timestamp: new Date(Date.now() - 86400000 * 1.5).toISOString(),
    details: 'Test report document contained exposed live Stripe secret key sk_live_•••••••.',
  },
];

const INITIAL_IP_RULES: IPRule[] = [
  {
    id: 'ip-1',
    type: 'allow',
    ipOrCidr: '198.51.100.0/24',
    note: 'Headquarters Primary Fibre Gateway',
    expiresAt: 'Never',
    addedBy: 'Super Admin',
    status: 'active',
    createdAt: '2026-02-01T10:00:00Z',
  },
  {
    id: 'ip-2',
    type: 'allow',
    ipOrCidr: '203.0.113.45',
    note: 'Executive Remote Gateway (Frankfurt)',
    expiresAt: '2026-12-31',
    addedBy: 'Security Lead',
    status: 'active',
    createdAt: '2026-04-12T14:00:00Z',
  },
  {
    id: 'ip-3',
    type: 'block',
    ipOrCidr: '185.220.101.0/24',
    note: 'Known Tor Exit Nodes & Credential Stuffing Origin',
    expiresAt: 'Never',
    addedBy: 'Automated Defense',
    status: 'active',
    createdAt: '2026-08-01T08:00:00Z',
  },
];

const INITIAL_GEO_RULES: GeoFenceRule = {
  id: 'geo-1',
  type: 'allow',
  countries: ['US', 'GB', 'IN', 'DE', 'CA', 'AU', 'SG', 'JP'],
  action: 'block',
  exceptions: {
    roles: ['super_admin'],
    users: ['ceo@teamone.com', 'cto@teamone.com'],
  },
  status: 'active',
  updatedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
};

const INITIAL_ANOMALY_SETTINGS: AnomalySettings = {
  impossibleTravel: { enabled: true, sensitivity: 'high', action: 'force_mfa' },
  unusualHours: { enabled: true, sensitivity: 'medium', action: 'alert' },
  newDevice: { enabled: true, sensitivity: 'medium', action: 'force_mfa' },
  bruteForce: { enabled: true, sensitivity: 'high', action: 'lock_account' },
  massDataExport: { enabled: true, sensitivity: 'high', action: 'block' },
};

const INITIAL_ANOMALY_ALERTS: AnomalyAlert[] = [
  {
    id: 'alt-401',
    type: 'Impossible Travel Velocity',
    riskScore: 89,
    userEmail: 'dev.lead@teamone.com',
    location: 'London, UK → Tokyo, JP (within 18m)',
    ipAddress: '194.26.29.112',
    status: 'open',
    detectedAt: new Date(Date.now() - 3600000 * 1.2).toISOString(),
    actionTaken: 'Session terminated; MFA re-verification triggered',
  },
  {
    id: 'alt-402',
    type: 'Brute Force / Password Spraying',
    riskScore: 95,
    userEmail: 'admin.support@teamone.com',
    location: 'St. Petersburg, RU',
    ipAddress: '91.240.118.80',
    status: 'investigating',
    detectedAt: new Date(Date.now() - 3600000 * 3.5).toISOString(),
    actionTaken: 'Account locked after 12 consecutive failed authentications',
  },
  {
    id: 'alt-403',
    type: 'Mass PII Export Volume Exceeded',
    riskScore: 78,
    userEmail: 'analytics.contractor@external.org',
    location: 'New York, US',
    ipAddress: '192.0.2.14',
    status: 'resolved',
    detectedAt: new Date(Date.now() - 86400000 * 1.1).toISOString(),
    actionTaken: 'Export throttled; alert dispatched to security team',
  },
];

const INITIAL_SECURITY_AUDIT: SecurityAuditItem[] = [
  {
    id: 'sec-aud-1',
    action: 'MFA Policy Updated',
    section: 'auth',
    performedBy: 'Super Admin',
    target: 'Global MFA Enforcement',
    oldValue: 'Grace period: 14 days',
    newValue: 'Grace period: 7 days, WebAuthn added',
    timestamp: new Date(Date.now() - 3600000 * 5).toISOString(),
  },
  {
    id: 'sec-aud-2',
    action: 'IP Blocklist Appended',
    section: 'threat_protection',
    performedBy: 'Automated Defense',
    target: '185.220.101.0/24',
    newValue: 'CIDR blocked due to credential stuffing',
    timestamp: new Date(Date.now() - 3600000 * 12).toISOString(),
  },
  {
    id: 'sec-aud-3',
    action: 'DLP Incident Marked Resolved',
    section: 'data_protection',
    performedBy: 'Security Officer',
    target: 'inc-102',
    oldValue: 'status: open',
    newValue: 'status: resolved',
    timestamp: new Date(Date.now() - 3600000 * 21).toISOString(),
  },
];

// -------------------------------------------------------------
// 3. Zustand Store Definition
// -------------------------------------------------------------

interface SecurityStoreState {
  // State slices
  ssoProviders: SSOProvider[];
  mfaSettings: MFASettings;
  passwordPolicy: PasswordPolicy;
  abacPolicies: ABACPolicy[];
  encryptionSettings: EncryptionSettings;
  encryptionKeys: EncryptionKey[];
  maskingRules: MaskingRule[];
  dlpRules: DLPRule[];
  dlpIncidents: DLPIncident[];
  ipRules: IPRule[];
  geoFenceRules: GeoFenceRule;
  anomalySettings: AnomalySettings;
  anomalyAlerts: AnomalyAlert[];
  securityAuditLogs: SecurityAuditItem[];
  isSaving: boolean;
  hasUnsavedChanges: Record<string, boolean>;

  // Actions - Authentication
  updateSSOProvider: (provider: SSOProvider) => Promise<void>;
  createSSOProvider: (provider: Omit<SSOProvider, 'id' | 'createdAt'>) => Promise<void>;
  deleteSSOProvider: (id: string) => Promise<void>;
  testSSOConnection: (id: string) => Promise<{ success: boolean; latencyMs: number; message: string }>;
  toggleSSOProvider: (id: string) => Promise<void>;

  updateMFASettings: (settings: Partial<MFASettings>) => Promise<void>;
  resetUserMFA: (userId: string, userEmail: string) => Promise<void>;

  updatePasswordPolicy: (policy: Partial<PasswordPolicy>) => Promise<void>;

  // Actions - Authorization & ABAC
  createABACPolicy: (policy: Omit<ABACPolicy, 'id' | 'updatedAt' | 'updatedBy'>) => Promise<void>;
  updateABACPolicy: (policy: ABACPolicy) => Promise<void>;
  deleteABACPolicy: (id: string) => Promise<void>;
  toggleABACPolicy: (id: string) => Promise<void>;
  duplicateABACPolicy: (id: string) => Promise<void>;
  simulateABAC: (params: {
    user: { id: string; email: string; department: string; location: string; clearance: string };
    resource: { type: string; sensitivity: string; owner: string };
    env: { time: string; device: string; network: string };
    action: string;
  }) => { decision: 'ALLOW' | 'DENY'; matchingPolicy?: ABACPolicy; reason: string };

  // Actions - Data Protection
  updateEncryptionSettings: (settings: Partial<EncryptionSettings>) => Promise<void>;
  createEncryptionKey: (key: Omit<EncryptionKey, 'id' | 'createdAt' | 'lastRotatedAt'>) => Promise<void>;
  rotateEncryptionKey: (keyId: string) => Promise<void>;
  disableEncryptionKey: (keyId: string) => Promise<void>;
  deleteEncryptionKey: (keyId: string) => Promise<void>;

  createMaskingRule: (rule: Omit<MaskingRule, 'id' | 'updatedAt'>) => Promise<void>;
  updateMaskingRule: (rule: MaskingRule) => Promise<void>;
  deleteMaskingRule: (id: string) => Promise<void>;
  toggleMaskingRule: (id: string) => Promise<void>;

  updateDLPRule: (rule: DLPRule) => Promise<void>;
  resolveDLPIncident: (id: string) => Promise<void>;

  // Actions - Threat Protection
  createIPRule: (rule: Omit<IPRule, 'id' | 'createdAt'>) => Promise<void>;
  deleteIPRule: (id: string) => Promise<void>;
  toggleIPRule: (id: string) => Promise<void>;
  bulkImportIPs: (ipsText: string, type: 'allow' | 'block') => Promise<number>;

  updateGeoFenceRules: (rules: Partial<GeoFenceRule>) => Promise<void>;

  updateAnomalySettings: (settings: Partial<AnomalySettings>) => Promise<void>;
  resolveAnomalyAlert: (id: string) => Promise<void>;
  lockUserFromAlert: (id: string, userEmail: string) => Promise<void>;

  // Helper Audit
  recordAudit: (action: string, section: SecurityAuditItem['section'], target: string, oldVal?: string, newVal?: string) => void;
  markSectionDirty: (section: string, isDirty: boolean) => void;
  resetAllDefaults: () => void;
}

export const useSecurityStore = create<SecurityStoreState>()(
  persist(
    (set, get) => ({
      ssoProviders: INITIAL_SSO_PROVIDERS,
      mfaSettings: INITIAL_MFA,
      passwordPolicy: INITIAL_PW_POLICY,
      abacPolicies: INITIAL_ABAC_POLICIES,
      encryptionSettings: INITIAL_ENCRYPTION_SETTINGS,
      encryptionKeys: INITIAL_KEYS,
      maskingRules: INITIAL_MASKING_RULES,
      dlpRules: INITIAL_DLP_RULES,
      dlpIncidents: INITIAL_DLP_INCIDENTS,
      ipRules: INITIAL_IP_RULES,
      geoFenceRules: INITIAL_GEO_RULES,
      anomalySettings: INITIAL_ANOMALY_SETTINGS,
      anomalyAlerts: INITIAL_ANOMALY_ALERTS,
      securityAuditLogs: INITIAL_SECURITY_AUDIT,
      isSaving: false,
      hasUnsavedChanges: {},

      markSectionDirty: (section, isDirty) => {
        set((state) => ({
          hasUnsavedChanges: { ...state.hasUnsavedChanges, [section]: isDirty },
        }));
      },

      recordAudit: (action, section, target, oldVal, newVal) => {
        const item: SecurityAuditItem = {
          id: `sec-aud-${Date.now()}`,
          action,
          section,
          performedBy: 'Current Administrator',
          target,
          oldValue: oldVal,
          newValue: newVal,
          timestamp: new Date().toISOString(),
        };
        set((state) => ({
          securityAuditLogs: [item, ...state.securityAuditLogs],
        }));
      },

      // ---------------------------------------------
      // SSO Provider Methods
      // ---------------------------------------------
      createSSOProvider: async (provider) => {
        const newProvider: SSOProvider = {
          ...provider,
          id: `sso-${Date.now()}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          ssoProviders: [...state.ssoProviders, newProvider],
        }));
        get().recordAudit('Create SSO Provider', 'auth', newProvider.name, undefined, `Type: ${newProvider.type}`);
        toast.success(`Identity Provider "${newProvider.name}" created`);
      },

      updateSSOProvider: async (updated) => {
        const prev = get().ssoProviders.find((p) => p.id === updated.id);
        set((state) => ({
          ssoProviders: state.ssoProviders.map((p) => (p.id === updated.id ? updated : p)),
        }));
        get().recordAudit('Update SSO Provider', 'auth', updated.name, prev?.status, updated.status);
        toast.success(`Identity Provider "${updated.name}" updated`);
      },

      deleteSSOProvider: async (id) => {
        const target = get().ssoProviders.find((p) => p.id === id);
        set((state) => ({
          ssoProviders: state.ssoProviders.filter((p) => p.id !== id),
        }));
        if (target) {
          get().recordAudit('Delete SSO Provider', 'auth', target.name, 'Active/Inactive', 'Deleted');
        }
        toast.success('Identity Provider removed');
      },

      toggleSSOProvider: async (id) => {
        const target = get().ssoProviders.find((p) => p.id === id);
        if (!target) return;
        const newStatus = target.status === 'active' ? 'inactive' : 'active';
        const newEnabled = newStatus === 'active';
        set((state) => ({
          ssoProviders: state.ssoProviders.map((p) =>
            p.id === id ? { ...p, status: newStatus, enabled: newEnabled } : p
          ),
        }));
        get().recordAudit('Toggle SSO Status', 'auth', target.name, target.status, newStatus);
        toast.success(`Provider is now ${newStatus}`);
      },

      testSSOConnection: async (id) => {
        const target = get().ssoProviders.find((p) => p.id === id);
        if (!target) return { success: false, latencyMs: 0, message: 'Provider not found' };

        // Simulate network handshake latency
        const latency = Math.floor(Math.random() * 60) + 25;
        const success = Boolean(target.metadataUrl && target.acsUrl);

        set((state) => ({
          ssoProviders: state.ssoProviders.map((p) =>
            p.id === id ? { ...p, lastTestedAt: new Date().toISOString(), testLatencyMs: latency } : p
          ),
        }));

        get().recordAudit('Test SSO Handshake', 'auth', target.name, undefined, `Latency: ${latency}ms`);
        return {
          success,
          latencyMs: latency,
          message: success
            ? `Handshake successful. SAML/OIDC metadata verified in ${latency}ms.`
            : 'Connection verification failed: Invalid or unreachable metadata URL.',
        };
      },

      // ---------------------------------------------
      // MFA Methods
      // ---------------------------------------------
      updateMFASettings: async (patch) => {
        set((state) => ({
          mfaSettings: { ...state.mfaSettings, ...patch },
        }));
        get().recordAudit('Update MFA Policy', 'auth', 'Global Multi-Factor Auth', undefined, JSON.stringify(patch));
        get().markSectionDirty('auth-mfa', false);
        toast.success('MFA configuration saved');
      },

      resetUserMFA: async (userId, userEmail) => {
        try {
          await apiClient.patch(`/admin/users/${userId}/mfa`, { mfa_enabled: false });
        } catch (_) {}
        get().recordAudit('Reset User MFA', 'auth', userEmail, 'Enrolled', 'MFA Cleared');
        toast.success(`MFA reset for user ${userEmail}. Next login will prompt re-enrollment.`);
      },

      // ---------------------------------------------
      // Password Policy Methods
      // ---------------------------------------------
      updatePasswordPolicy: async (patch) => {
        set((state) => ({
          passwordPolicy: { ...state.passwordPolicy, ...patch },
        }));
        get().recordAudit('Update Password Policy', 'auth', 'Global Password & Session Policy', undefined, JSON.stringify(patch));
        get().markSectionDirty('auth-password', false);
        toast.success('Password & Session policy updated');
      },

      // ---------------------------------------------
      // ABAC Authorization Methods
      // ---------------------------------------------
      createABACPolicy: async (policyData) => {
        const newPolicy: ABACPolicy = {
          ...policyData,
          id: `abac-${Date.now()}`,
          updatedAt: new Date().toISOString(),
          updatedBy: 'Admin User',
        };
        set((state) => ({
          abacPolicies: [...state.abacPolicies, newPolicy],
        }));
        get().recordAudit('Create ABAC Policy', 'abac', newPolicy.name, undefined, `Effect: ${newPolicy.effect}`);
        toast.success(`ABAC Policy "${newPolicy.name}" created`);
      },

      updateABACPolicy: async (updated) => {
        const prev = get().abacPolicies.find((p) => p.id === updated.id);
        const next: ABACPolicy = {
          ...updated,
          updatedAt: new Date().toISOString(),
          updatedBy: 'Admin User',
        };
        set((state) => ({
          abacPolicies: state.abacPolicies.map((p) => (p.id === updated.id ? next : p)),
        }));
        get().recordAudit('Update ABAC Policy', 'abac', updated.name, prev?.effect, next.effect);
        toast.success(`ABAC Policy "${updated.name}" updated`);
      },

      deleteABACPolicy: async (id) => {
        const target = get().abacPolicies.find((p) => p.id === id);
        set((state) => ({
          abacPolicies: state.abacPolicies.filter((p) => p.id !== id),
        }));
        if (target) {
          get().recordAudit('Delete ABAC Policy', 'abac', target.name, target.effect, 'Deleted');
        }
        toast.success('ABAC Policy deleted');
      },

      toggleABACPolicy: async (id) => {
        const target = get().abacPolicies.find((p) => p.id === id);
        if (!target) return;
        const newStatus = target.status === 'active' ? 'inactive' : 'active';
        set((state) => ({
          abacPolicies: state.abacPolicies.map((p) => (p.id === id ? { ...p, status: newStatus } : p)),
        }));
        get().recordAudit('Toggle ABAC Policy', 'abac', target.name, target.status, newStatus);
        toast.success(`Policy "${target.name}" is now ${newStatus}`);
      },

      duplicateABACPolicy: async (id) => {
        const target = get().abacPolicies.find((p) => p.id === id);
        if (!target) return;
        const copy: ABACPolicy = {
          ...target,
          id: `abac-${Date.now()}`,
          name: `${target.name} (Copy)`,
          updatedAt: new Date().toISOString(),
          updatedBy: 'Admin User',
        };
        set((state) => ({
          abacPolicies: [...state.abacPolicies, copy],
        }));
        get().recordAudit('Duplicate ABAC Policy', 'abac', copy.name, target.name, 'Cloned');
        toast.success(`Cloned policy "${copy.name}"`);
      },

      simulateABAC: (params) => {
        const { user, resource, env } = params;
        const activePolicies = get()
          .abacPolicies.filter((p) => p.status === 'active')
          .sort((a, b) => a.priority - b.priority);

        // Attribute lookup mapping
        const attributeMap: Record<string, string> = {
          'user.department': user.department || '',
          'user.location': user.location || '',
          'user.clearance': user.clearance || '',
          'resource.sensitivity': resource.sensitivity || '',
          'resource.type': resource.type || '',
          'resource.owner': resource.owner || '',
          'env.time': env.time || '',
          'env.device': env.device || '',
          'env.network': env.network || '',
        };

        let matchedPolicy: ABACPolicy | undefined;
        let matchedEffect: 'ALLOW' | 'DENY' = 'ALLOW';

        for (const policy of activePolicies) {
          let conditionsMatch = false;

          if (policy.conditions.length === 0) {
            conditionsMatch = true;
          } else if (policy.logic === 'AND') {
            conditionsMatch = policy.conditions.every((cond) => {
              const actual = (attributeMap[cond.field] || '').toLowerCase().trim();
              const expected = cond.value.toLowerCase().trim();
              if (cond.operator === 'equals') return actual === expected;
              if (cond.operator === 'not_equals') return actual !== expected;
              if (cond.operator === 'contains') return actual.includes(expected);
              if (cond.operator === 'in') {
                const list = expected.split(',').map((s) => s.trim());
                return list.includes(actual);
              }
              if (cond.operator === 'not_in') {
                const list = expected.split(',').map((s) => s.trim());
                return !list.includes(actual);
              }
              return false;
            });
          } else {
            // OR logic
            conditionsMatch = policy.conditions.some((cond) => {
              const actual = (attributeMap[cond.field] || '').toLowerCase().trim();
              const expected = cond.value.toLowerCase().trim();
              if (cond.operator === 'equals') return actual === expected;
              if (cond.operator === 'not_equals') return actual !== expected;
              if (cond.operator === 'contains') return actual.includes(expected);
              if (cond.operator === 'in') {
                const list = expected.split(',').map((s) => s.trim());
                return list.includes(actual);
              }
              if (cond.operator === 'not_in') {
                const list = expected.split(',').map((s) => s.trim());
                return !list.includes(actual);
              }
              return false;
            });
          }

          if (conditionsMatch) {
            matchedPolicy = policy;
            matchedEffect = policy.effect;
            // CRITICAL: DENY overrides ALLOW
            if (policy.effect === 'DENY') {
              break;
            }
          }
        }

        if (matchedPolicy) {
          return {
            decision: matchedEffect,
            matchingPolicy: matchedPolicy,
            reason:
              matchedEffect === 'DENY'
                ? `Access denied by policy "${matchedPolicy.name}" (Priority ${matchedPolicy.priority}) due to strict condition match.`
                : `Access granted by policy "${matchedPolicy.name}" (Priority ${matchedPolicy.priority}).`,
          };
        }

        return {
          decision: 'ALLOW',
          reason: 'Default Access Granted: No restricting ABAC policy conditions were triggered.',
        };
      },

      // ---------------------------------------------
      // Data Protection Methods
      // ---------------------------------------------
      updateEncryptionSettings: async (patch) => {
        set((state) => ({
          encryptionSettings: { ...state.encryptionSettings, ...patch },
        }));
        get().recordAudit('Update Encryption Settings', 'data_protection', 'Cryptographic Engine', undefined, JSON.stringify(patch));
        toast.success('Encryption settings saved');
      },

      createEncryptionKey: async (keyData) => {
        const newKey: EncryptionKey = {
          ...keyData,
          id: `key-${Date.now()}`,
          createdAt: new Date().toISOString(),
          lastRotatedAt: new Date().toISOString(),
        };
        set((state) => ({
          encryptionKeys: [...state.encryptionKeys, newKey],
        }));
        get().recordAudit('Create KMS Key', 'data_protection', newKey.keyId, undefined, newKey.algorithm);
        toast.success(`Key ${newKey.keyId} created`);
      },

      rotateEncryptionKey: async (id) => {
        const now = new Date().toISOString();
        set((state) => ({
          encryptionKeys: state.encryptionKeys.map((k) => (k.id === id ? { ...k, lastRotatedAt: now } : k)),
        }));
        const target = get().encryptionKeys.find((k) => k.id === id);
        get().recordAudit('Rotate KMS Key', 'data_protection', target?.keyId || id, undefined, `Rotated at ${now}`);
        toast.success(`Key ${target?.keyId || id} successfully rotated`);
      },

      disableEncryptionKey: async (id) => {
        set((state) => ({
          encryptionKeys: state.encryptionKeys.map((k) => (k.id === id ? { ...k, status: 'retired' } : k)),
        }));
        const target = get().encryptionKeys.find((k) => k.id === id);
        get().recordAudit('Disable KMS Key', 'data_protection', target?.keyId || id, 'active', 'retired');
        toast.success(`Key ${target?.keyId || id} retired`);
      },

      deleteEncryptionKey: async (id) => {
        const target = get().encryptionKeys.find((k) => k.id === id);
        set((state) => ({
          encryptionKeys: state.encryptionKeys.filter((k) => k.id !== id),
        }));
        get().recordAudit('Delete KMS Key', 'data_protection', target?.keyId || id, target?.status, 'Deleted');
        toast.success('Encryption key removed');
      },

      createMaskingRule: async (ruleData) => {
        const newRule: MaskingRule = {
          ...ruleData,
          id: `mask-${Date.now()}`,
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({
          maskingRules: [...state.maskingRules, newRule],
        }));
        get().recordAudit('Create Masking Rule', 'data_protection', newRule.columnName, undefined, newRule.maskType);
        toast.success(`Masking rule for "${newRule.columnName}" created`);
      },

      updateMaskingRule: async (updated) => {
        const next: MaskingRule = { ...updated, updatedAt: new Date().toISOString() };
        set((state) => ({
          maskingRules: state.maskingRules.map((m) => (m.id === updated.id ? next : m)),
        }));
        get().recordAudit('Update Masking Rule', 'data_protection', updated.columnName, undefined, updated.maskType);
        toast.success(`Masking rule for "${updated.columnName}" updated`);
      },

      deleteMaskingRule: async (id) => {
        const target = get().maskingRules.find((m) => m.id === id);
        set((state) => ({
          maskingRules: state.maskingRules.filter((m) => m.id !== id),
        }));
        get().recordAudit('Delete Masking Rule', 'data_protection', target?.columnName || id, undefined, 'Deleted');
        toast.success('Masking rule removed');
      },

      toggleMaskingRule: async (id) => {
        const target = get().maskingRules.find((m) => m.id === id);
        if (!target) return;
        const newStatus = target.status === 'active' ? 'inactive' : 'active';
        set((state) => ({
          maskingRules: state.maskingRules.map((m) => (m.id === id ? { ...m, status: newStatus } : m)),
        }));
        get().recordAudit('Toggle Masking Rule', 'data_protection', target.columnName, target.status, newStatus);
        toast.success(`Masking for "${target.columnName}" is now ${newStatus}`);
      },

      updateDLPRule: async (updated) => {
        set((state) => ({
          dlpRules: state.dlpRules.map((r) => (r.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : r)),
        }));
        get().recordAudit('Update DLP Rule', 'data_protection', updated.name, undefined, updated.action);
        toast.success(`DLP Rule "${updated.name}" updated`);
      },

      resolveDLPIncident: async (id) => {
        set((state) => ({
          dlpIncidents: state.dlpIncidents.map((inc) => (inc.id === id ? { ...inc, status: 'resolved' } : inc)),
        }));
        get().recordAudit('Resolve DLP Incident', 'data_protection', id, 'open', 'resolved');
        toast.success('Incident resolved');
      },

      // ---------------------------------------------
      // Threat Protection Methods
      // ---------------------------------------------
      createIPRule: async (ruleData) => {
        const newRule: IPRule = {
          ...ruleData,
          id: `ip-${Date.now()}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          ipRules: [...state.ipRules, newRule],
        }));
        get().recordAudit('Create IP Rule', 'threat_protection', newRule.ipOrCidr, undefined, newRule.type);
        toast.success(`${newRule.type === 'allow' ? 'Allowlist' : 'Blocklist'} entry added for ${newRule.ipOrCidr}`);
      },

      deleteIPRule: async (id) => {
        const target = get().ipRules.find((r) => r.id === id);
        set((state) => ({
          ipRules: state.ipRules.filter((r) => r.id !== id),
        }));
        get().recordAudit('Delete IP Rule', 'threat_protection', target?.ipOrCidr || id, undefined, 'Deleted');
        toast.success('IP entry removed');
      },

      toggleIPRule: async (id) => {
        const target = get().ipRules.find((r) => r.id === id);
        if (!target) return;
        const newStatus = target.status === 'active' ? 'inactive' : 'active';
        set((state) => ({
          ipRules: state.ipRules.map((r) => (r.id === id ? { ...r, status: newStatus } : r)),
        }));
        get().recordAudit('Toggle IP Rule', 'threat_protection', target.ipOrCidr, target.status, newStatus);
        toast.success(`IP entry is now ${newStatus}`);
      },

      bulkImportIPs: async (ipsText, type) => {
        const lines = ipsText
          .split(/[\n,;]+/)
          .map((s) => s.trim())
          .filter(Boolean);

        const newEntries: IPRule[] = lines.map((ip, index) => ({
          id: `ip-${Date.now()}-${index}`,
          type,
          ipOrCidr: ip,
          note: `Bulk imported on ${new Date().toLocaleDateString()}`,
          expiresAt: 'Never',
          addedBy: 'Administrator',
          status: 'active',
          createdAt: new Date().toISOString(),
        }));

        set((state) => ({
          ipRules: [...state.ipRules, ...newEntries],
        }));

        get().recordAudit('Bulk Import IPs', 'threat_protection', `${newEntries.length} entries`, undefined, type);
        toast.success(`Successfully imported ${newEntries.length} IP entries`);
        return newEntries.length;
      },

      updateGeoFenceRules: async (patch) => {
        set((state) => ({
          geoFenceRules: { ...state.geoFenceRules, ...patch, updatedAt: new Date().toISOString() },
        }));
        get().recordAudit('Update Geo-Fence Policy', 'threat_protection', 'Global Geo-Fence', undefined, JSON.stringify(patch));
        toast.success('Geo-fence configuration saved');
      },

      updateAnomalySettings: async (patch) => {
        set((state) => ({
          anomalySettings: { ...state.anomalySettings, ...patch },
        }));
        get().recordAudit('Update Anomaly Engine', 'threat_protection', 'AI Anomaly Detector', undefined, JSON.stringify(patch));
        toast.success('Anomaly detection thresholds saved');
      },

      resolveAnomalyAlert: async (id) => {
        set((state) => ({
          anomalyAlerts: state.anomalyAlerts.map((alt) => (alt.id === id ? { ...alt, status: 'resolved' } : alt)),
        }));
        get().recordAudit('Resolve Threat Alert', 'threat_protection', id, 'open', 'resolved');
        toast.success('Security alert marked as resolved');
      },

      lockUserFromAlert: async (alertId, userEmail) => {
        set((state) => ({
          anomalyAlerts: state.anomalyAlerts.map((alt) =>
            alt.id === alertId ? { ...alt, status: 'investigating', actionTaken: 'Account locked by Administrator' } : alt
          ),
        }));
        get().recordAudit('Lock User Account', 'threat_protection', userEmail, 'Active', 'Account Locked');
        toast.success(`Account for ${userEmail} has been locked`);
      },

      resetAllDefaults: () => {
        set({
          ssoProviders: INITIAL_SSO_PROVIDERS,
          mfaSettings: INITIAL_MFA,
          passwordPolicy: INITIAL_PW_POLICY,
          abacPolicies: INITIAL_ABAC_POLICIES,
          encryptionSettings: INITIAL_ENCRYPTION_SETTINGS,
          encryptionKeys: INITIAL_KEYS,
          maskingRules: INITIAL_MASKING_RULES,
          dlpRules: INITIAL_DLP_RULES,
          dlpIncidents: INITIAL_DLP_INCIDENTS,
          ipRules: INITIAL_IP_RULES,
          geoFenceRules: INITIAL_GEO_RULES,
          anomalySettings: INITIAL_ANOMALY_SETTINGS,
          anomalyAlerts: INITIAL_ANOMALY_ALERTS,
        });
        toast.success('Reset all security settings to enterprise defaults');
      },
    }),
    {
      name: 'teamone-security-storage',
    }
  )
);
